import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { AdminNav } from "@/components/admin/AdminNav";

interface Source {
  id: string;
  kind: string;
  name: string;
  url: string;
  trust_score: number;
  active: number;
  last_polled_at: number | null;
  last_error: string | null;
}

export function AdminSources() {
  const api = useApi();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["admin-sources"],
    queryFn: () => api.get<{ sources: Source[] }>("/admin/sources"),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/admin/sources/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-sources"] }),
  });

  return (
    <div className="px-10 py-10 max-w-6xl">
      <header className="mb-8">
        <p className="label-eyebrow">Operations</p>
        <h1 className="mt-2 text-display-2 font-display">Sources</h1>
        <p className="mt-3 text-whisper text-sm max-w-xl leading-relaxed">
          Toggle active state and adjust trust scores. Trust score (0–1) weights
          source attribution in the intelligence feed.
        </p>
      </header>

      <AdminNav />

      {list.isLoading ? (
        <p className="text-sm text-whisper">Reading sources…</p>
      ) : (list.data?.sources.length ?? 0) === 0 ? (
        <p className="text-sm text-whisper">No sources configured.</p>
      ) : (
        <div className="panel divide-y divide-veil/40">
          {list.data?.sources.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              busy={patch.isPending}
              onToggle={() => patch.mutate({ id: s.id, body: { active: !s.active } })}
              onTrust={(v) => patch.mutate({ id: s.id, body: { trust_score: v } })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SourceRow({
  source,
  busy,
  onToggle,
  onTrust,
}: {
  source: Source;
  busy: boolean;
  onToggle: () => void;
  onTrust: (v: number) => void;
}) {
  const [trust, setTrust] = useState(source.trust_score);
  const isActive = source.active === 1;
  const lastPolled = source.last_polled_at
    ? new Date(source.last_polled_at).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "never";

  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-6 px-6 py-4">
      <div className="min-w-0">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-sm">{source.name}</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted">
            {source.kind}
          </span>
          {!isActive && (
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted">paused</span>
          )}
        </div>
        <div className="mt-1 text-xs text-whisper truncate">{source.url}</div>
        {source.last_error && (
          <div className="mt-1 text-xs text-gold-glow line-clamp-1">
            ⚠ {source.last_error}
          </div>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted text-right whitespace-nowrap">
        <div className="tabular-nums text-whisper">Polled</div>
        <div>{lastPolled}</div>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[10px] uppercase tracking-[0.18em] text-muted">trust</label>
        <input
          type="number"
          min={0}
          max={1}
          step={0.05}
          value={trust}
          onChange={(e) => setTrust(Number(e.target.value))}
          onBlur={() => trust !== source.trust_score && onTrust(trust)}
          disabled={busy}
          className="w-16 bg-ink-elev/60 border border-veil/50 rounded px-2 py-1 text-xs text-fog tabular-nums focus:outline-none focus:border-gold/50"
        />
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={busy}
        className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog transition-colors disabled:opacity-40"
      >
        {isActive ? "Pause" : "Enable"}
      </button>
    </div>
  );
}
